<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex align-items-center flex-wrap gap-2">
              <h6 class="mb-0 me-auto">Órdenes de Servicio</h6>
              <router-link to="/service-orders/create" class="btn btn-sm btn-primary">
                <i class="fa fa-plus me-1"></i> Nueva orden
              </router-link>
            </div>
          </div>
          <div class="card-body px-0 pt-0 pb-2">
            <div class="px-4 pt-3 pb-2">
              <div class="row g-2">
                <div class="col-md-3">
                  <label class="form-control-label">Estado</label>
                  <select class="form-select" v-model="filters.status" @change="fetchData">
                    <option value="">Todos</option>
                    <option value="0">Borrador</option>
                    <option value="1">Formalizada</option>
                    <option value="2">Rechazada</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label">Desde</label>
                  <input type="date" class="form-control" v-model="filters.start_date" @change="fetchData" />
                </div>
                <div class="col-md-3">
                  <label class="form-control-label">Hasta</label>
                  <input type="date" class="form-control" v-model="filters.end_date" @change="fetchData" />
                </div>
                <div class="col-md-3">
                  <label class="form-control-label">Buscar</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="filters.search"
                    placeholder="Cliente, placa, teléfono..."
                    @input="fetchData"
                  />
                </div>
              </div>
            </div>

            <div class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Cliente</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Teléfono</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Moto</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Placa</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Entrada</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Técnico</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Estado</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in data" :key="row.id">
                    <td>
                      <h6 class="mb-0 text-sm">{{ row.customer_name }}</h6>
                    </td>
                    <td class="text-sm">{{ row.phone || '—' }}</td>
                    <td class="text-sm">{{ motorcycleLabel(row) }}</td>
                    <td class="align-middle text-center text-sm">{{ row.license_plate || '—' }}</td>
                    <td class="align-middle text-center text-sm">{{ formatDate(row.entry_at) }}</td>
                    <td class="align-middle text-center text-sm">{{ row.assigned_technician || '—' }}</td>
                    <td class="align-middle text-center">
                      <span
                        class="badge badge-sm"
                        :class="statusBadgeClass(row.status)"
                      >
                        {{ statusLabel(row.status) }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <router-link
                        :to="`/service-orders/${row.id}/edit`"
                        class="btn btn-link text-secondary mb-0 px-2"
                        title="Editar"
                      >
                        <i class="fa fa-edit text-xs"></i>
                      </router-link>
                      <button
                        v-if="row.status !== 1"
                        type="button"
                        class="btn btn-link text-danger mb-0 px-2"
                        title="Eliminar"
                        @click="confirmDelete(row)"
                      >
                        <i class="fa fa-trash text-xs"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!data || data.length === 0">
                    <td colspan="8" class="text-center text-sm text-secondary py-4">No hay órdenes registradas</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="px-4 py-3" v-if="pagination.lastPage > 1">
              <nav>
                <ul class="pagination pagination-sm justify-content-end mb-0">
                  <li class="page-item" :class="{ disabled: pagination.currentPage <= 1 }">
                    <button class="page-link" @click="goPage(pagination.currentPage - 1)">Anterior</button>
                  </li>
                  <li class="page-item disabled">
                    <span class="page-link">{{ pagination.currentPage }} / {{ pagination.lastPage }}</span>
                  </li>
                  <li class="page-item" :class="{ disabled: pagination.currentPage >= pagination.lastPage }">
                    <button class="page-link" @click="goPage(pagination.currentPage + 1)">Siguiente</button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Swal from "sweetalert2";

export default {
  name: "ServiceOrdersTable",
  data() {
    return {
      filters: {
        search: "",
        status: "",
        start_date: "",
        end_date: "",
        page: 1,
        per_page: 10,
      },
    };
  },
  computed: {
    ...mapGetters("serviceOrders", ["allData", "pagination"]),
    currentUser() {
      try {
        return JSON.parse(localStorage.getItem("user") || "{}");
      } catch {
        return {};
      }
    },
    isFieldWorker() {
      const role = String(this.currentUser?.role ?? "");
      return role === "3" || role === "6";
    },
    data() {
      const rows = this.allData || [];
      if (!this.isFieldWorker || !this.currentUser?.id) {
        return rows;
      }
      const uid = Number(this.currentUser.id);
      return rows.filter((row) => Number(row.user_id) === uid);
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    ...mapActions("serviceOrders", ["fetchData", "delete"]),
    fetchData() {
      const params = { ...this.filters };
      Object.keys(params).forEach((k) => {
        if (params[k] === "" || params[k] === null) delete params[k];
      });
      this.$store.dispatch("serviceOrders/fetchData", params);
    },
    goPage(page) {
      if (page < 1 || page > this.pagination.lastPage) return;
      this.filters.page = page;
      this.fetchData();
    },
    motorcycleLabel(row) {
      const parts = [row.motorcycle_brand, row.motorcycle_model, row.motorcycle_color].filter(Boolean);
      return parts.length ? parts.join(" · ") : "—";
    },
    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" });
    },
    statusLabel(status) {
      const s = Number(status);
      if (s === 1) return "Formalizada";
      if (s === 2) return "Rechazada";
      return "Borrador";
    },
    statusBadgeClass(status) {
      const s = Number(status);
      if (s === 1) return "bg-gradient-success";
      if (s === 2) return "bg-gradient-danger";
      return "bg-gradient-secondary";
    },
    confirmDelete(row) {
      Swal.fire({
        title: "¿Eliminar orden?",
        text: `Cliente: ${row.customer_name}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      }).then(async (result) => {
        if (!result.isConfirmed) return;
        try {
          await this.delete(row.id);
          Swal.fire("Eliminada", "La orden fue eliminada.", "success");
          this.fetchData();
        } catch {
          Swal.fire("Error", "No se pudo eliminar la orden.", "error");
        }
      });
    },
  },
};
</script>
